import { faHourglassStart } from "@fortawesome/pro-regular-svg-icons/faHourglassStart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Dialog, DialogTitle, Grid, Skeleton, Stack, styled } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppImages } from "app/appImages";
import { MprcRequestErrorDialog } from "components";
import { IconButtonClose } from "components/iconButtons";
import { ChannelAutoRegisterMode, FeedChannelHeaderResponse } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { LRUCache } from 'typescript-lru-cache';
import { ChannelDialogInfo } from "views/knowledge/channels/dialogs/ChannelDialogInfo";
import { PendingApprovalChannelButton, SubscribeChannelButton, ToggleMutedChannelButton, UnsubscribeChannelButton } from "views/knowledge/feed/FeedChannel/defaults";

////////////////////////////////////////////////////////////////////////////////

const MainContainer = styled("div")(
    ({ theme }) => ({
        height: "310px",
        width: "100%",
        minWidth: "716px",
        position: "relative",
        borderRadius: "16px",
        boxShadow: "2px 2px 20px rgba(0, 0, 0, 0.2)"
    })
);

const ContentContainer = styled("div")(
    ({ theme }) => ({
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        color: "white",
        fontWeight: "bold",
        position: "absolute",
        top: "205px",
        height: "105px",
        width: "100%",
        borderBottomLeftRadius: "16px",
        borderBottomRightRadius: "16px",
        boxShadow: "2px 2px 20px rgba(0, 0, 0, 0.2)"
    })
);

const BackgroundImage = styled(
    "div",
    { shouldForwardProp: (propName: string) => !["imageUrl"].includes(propName) }
)<{ imageUrl: string; }>(
    ({ theme, imageUrl }) => (
        {
            backgroundImage: `url("${imageUrl}")`,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            filter: "blur(40px)",
            WebkitFilter: "blur(40px)",
            height: "calc(100% - 8px)",
            width: "calc(100% - 8px)",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover"
        }
    )
);

/////////////////////////////////////////////////////////////////////////////////

const LoadingSkeleton = () => {
    return (
        <MainContainer>
            <BackgroundImage
                imageUrl={AppImages.KnowledgeChannelThumbnail}
            />

            <ContentContainer>
                <Grid container flexWrap="nowrap">
                    <Grid size="grow" padding={1} overflow="hidden">
                        <Grid container direction="column" flexWrap="nowrap">
                            <Grid overflow="hidden">
                                <Grid container justifyContent="space-between">
                                    <Grid size="grow">
                                    </Grid>
                                </Grid>
                                <Grid container spacing={0.5} alignItems="center">
                                    <Grid size={{ xs: 12 }}>
                                        <Skeleton variant="rectangular" width={310} height={22} />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Skeleton variant="rectangular" width={310} height={22} />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Skeleton variant="rectangular" width={310} height={22} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid>
                        <Box m={2}>
                            <Skeleton variant="rectangular" width={188} height={42} />
                        </Box>
                    </Grid>
                </Grid>
            </ContentContainer>
        </MainContainer>
    );
};

/////////////////////////////////////////////////////////////////////////////////

interface IHeaderProps {
    channel: FeedChannelHeaderResponse | null;
    onBeforeLinkClick?: () => void;
    onReset: (channel?: FeedChannelHeaderResponse) => void;
    onClose: () => void;
}

const ChannelHeader: React.FC<IHeaderProps> = (props) => {
    const { onBeforeLinkClick, onClose } = props;
    const channel = props.channel!;
    const { t } = useTranslation("knowledge");
    const navigate = useNavigate();

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        <ChannelDialogInfo
            title={channel.name}
            subtitle={[
                t("Feed.Channel.ChannelText"),
                t("Feed.Channel.TotalPublicationText", { count: channel.totalPublications }),
                t("Feed.Channel.TotalSubscribersText", { count: channel.totalSubscribers })
            ]}
            description={channel.description}
            thumbnailUrl={channel.thumbnailUrl}
            defaultThumbnail={AppImages.KnowledgeChannelThumbnail}
            buttons={
                <>
                    {
                        channel.registered
                            ?
                            <Stack direction="row" alignItems="center" gap={1}>
                                <UnsubscribeChannelButton
                                    channel={channel}
                                    onComplete={() => props.onReset()}
                                    isOwnerChannel={channel.isOwnerChannel}
                                />
                                {channel.pendingSubscription
                                    ?
                                    <FontAwesomeIcon
                                        className={AppGlobalClassKeys.clickable}
                                        icon={faHourglassStart}
                                        title={t("Subcriptions.Signup.WaitingProcessingTooltip")}
                                    />
                                    :
                                    <ToggleMutedChannelButton
                                        channel={props.channel}
                                        onSuccess={() => props.onReset()}
                                    />
                                }
                            </Stack>
                            : channel.pendingApproval
                                ? <PendingApprovalChannelButton />
                                : channel.autoRegisterMode != ChannelAutoRegisterMode.Forbidden && (
                                    <SubscribeChannelButton
                                        channel={channel}
                                        onComplete={() => props.onReset()}
                                    />
                                )
                    }
                </>
            }
            onNavigate={
                () => {
                    onBeforeLinkClick && onBeforeLinkClick();
                    onClose();
                    navigate(KnowledgePaths.FeedChannel(channel.channelId));
                }
            }
        />
    );
};

/////////////////////////////////////////////////////////////////////////////////

interface IPros {
    channelId: string | null;
    onClose: () => void;
    onBeforeLinkClick?: () => void;
}

export const ChannelDialog = (props: IPros) => {
    const {
        channelId,
        onClose,
        onBeforeLinkClick
    } = props;

    const api = useCoreApi();
    const [channel, setChannel] = useState<FeedChannelHeaderResponse>();
    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const cache = useRef<LRUCache<string, FeedChannelHeaderResponse>>(
        new LRUCache<string, FeedChannelHeaderResponse>({
            maxSize: 10,
            entryExpirationTimeInMS: 300000 // 5 minutos
        })
    );

    ///
    const load = (reload?: boolean) => {

        const u = cache.current.get(channelId!);
        if (u == null || reload) {
            setLoading(true);

            api.v1.knowledgeFeed.getFeedChannelHeader({ channelId: channelId! })
                .then((result) => {
                    cache.current.set(channelId!, result.data);
                    setChannel(result.data);
                })
                .catch((error: any) => {
                    setViewError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
        else {
            setChannel(u);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        if (channelId != null && (channel == undefined || channel.channelId != channelId))
            load();
    }, [channelId]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return channelId == null
        ? null
        : (
            <>
                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />

                <Dialog
                    open={channelId != null}
                    fullWidth
                    maxWidth="md"
                    onClose={onClose}
                >
                    <DialogTitle component="div" sx={{
                        m: 0,
                        p: 0,
                        textTransform: "unset",
                        "&.MuiDialogTitle-root+.MuiDialogContent-root": { paddingTop: 2 }
                    }}>
                        <Box position="relative">
                            <Box position="absolute" right="2px" zIndex="2">
                                <IconButtonClose color="white" size="large" onClick={onClose} />
                            </Box>
                            {
                                loading
                                    ? <LoadingSkeleton />
                                    : <ChannelHeader
                                        channel={channel!}
                                        onBeforeLinkClick={onBeforeLinkClick}
                                        onReset={() => load(true)}
                                        onClose={onClose}
                                    />
                            }
                        </Box>
                    </DialogTitle>
                </Dialog>
            </>
        );
};
